import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const ContactoPage = () => {
  // Estado para el formulario
  const [formState, setFormState] = useState({
    nombre: '',
    email: '',
    asunto: 'Información General',
    mensaje: ''
  });

  // Manejar cambios en los inputs
  const handleChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  // Manejar el envío del formulario (Redirección mailto)
  const handleSubmit = (e) => {
    e.preventDefault();
    const { nombre, email, asunto, mensaje } = formState;
    
    // Crear el cuerpo del correo
    const subject = encodeURIComponent(`Contacto Web: ${asunto}`);
    const body = encodeURIComponent(
      `Hola, mi nombre es ${nombre}.\n\nMi correo de contacto es: ${email}\n\nMensaje:\n${mensaje}`
    );

    // Abrir cliente de correo
    window.location.href = `mailto:info@avconsulting.com.pe?subject=${subject}&body=${body}`;
  };

  // Animation Variants (Consistent with ServicesPage)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Contacto | AV Global</title>  
        <meta name="description" content="Conoce al equipo de AV Global, nuestra misión y visión para conectar estudiantes peruanos con oportunidades internacionales."></meta>
      </Helmet>

      <div className="bg-white font-sans text-gray-800">
        
        {/* 1. HERO HEADER */}
        <section className="relative pt-24 pb-16 px-6 md:px-12 lg:px-16 overflow-hidden">
          {/* Decorative Background Blur */}
          <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-sky-100/50 blur-[120px] rounded-full pointer-events-none z-0"></div>

          <motion.div 
            className="max-w-7xl mx-auto relative z-10 text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="inline-block px-6 py-1.5 border border-gray-400 rounded-full mb-6 bg-white/50 backdrop-blur-sm"
              variants={itemVariants}
            >
              <span className="text-gray-600 text-sm font-medium">Contáctanos</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a3b5c] mb-6 leading-tight tracking-tight"
              variants={itemVariants}
            >
              Empecemos a construir <br /> <span className="text-[#5B9BB0]">tu futuro global.</span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Estamos listos para resolver tus dudas y diseñar el plan educativo perfecto para ti o tu institución.
            </motion.p>
          </motion.div>
        </section>

        {/* 2. CONTACT CONTENT */}
        <section className="pb-24 px-6 md:px-12 lg:px-16">
          <div className="max-w-[90rem] mx-auto">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
              
              {/* Left Column: Contact Info & Map */}
              <motion.div 
                className="flex flex-col gap-8"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                
                {/* Info Cards */}
                <motion.div className="bg-[#F8FAFC] rounded-[2.5rem] p-8 border border-slate-100" variants={itemVariants}>
                  <h3 className="text-2xl font-bold text-[#1a3b5c] mb-8">Información de Contacto</h3>
                  
                  <div className="space-y-8">
                    {/* Email */}
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-white text-[#5B9BB0] flex items-center justify-center shadow-sm border border-slate-100 flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 font-medium uppercase tracking-wide mb-1">Email</p>
                        <a href="mailto:info@avconsulting.com.pe" className="text-lg font-semibold text-gray-700 hover:text-[#1a3b5c] transition-colors">
                          info@avconsulting.com.pe
                        </a>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-white text-[#5B9BB0] flex items-center justify-center shadow-sm border border-slate-100 flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 font-medium uppercase tracking-wide mb-1">Teléfono</p>
                        <a href="tel:+51994381700" className="text-lg font-semibold text-gray-700 hover:text-[#1a3b5c] transition-colors">
                          +51 994 381 700
                        </a>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-white text-[#5B9BB0] flex items-center justify-center shadow-sm border border-slate-100 flex-shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 font-medium uppercase tracking-wide mb-1">Ubicación</p>
                        <p className="text-lg font-semibold text-gray-700">
                          San Isidro, Lima - Perú
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Google Maps Embed */}
                <motion.div 
                  className="h-64 md:h-80 rounded-[2.5rem] overflow-hidden shadow-sm relative group border border-slate-100"
                  variants={itemVariants}
                >
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15605.158145217426!2d-77.0425983870634!3d-12.092955586963471!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9105c8430e527f51%3A0xc6657156157e802a!2sSan%20Isidro%2C%20Lima!5e0!3m2!1ses-419!2spe!4v1710182400000!5m2!1ses-419!2spe" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full grayscale-[20%] hover:grayscale-0 transition-all duration-500"
                    title="Mapa San Isidro"
                  ></iframe>
                </motion.div>

              </motion.div>

              {/* Right Column: Form */}
              <motion.div 
                className="bg-white rounded-[3rem] p-8 md:p-12 shadow-xl shadow-slate-100 border border-slate-50"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
              >
                <motion.h2 className="text-3xl font-bold text-[#1a3b5c] mb-2" variants={itemVariants}>Envíanos un mensaje</motion.h2>
                <motion.p className="text-gray-500 mb-8" variants={itemVariants}>Completa el formulario y te responderemos a la brevedad.</motion.p>

                {/* FORM START */}
                <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                  
                  <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={itemVariants}>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-bold text-gray-700 ml-2">Nombre</label>
                      <input 
                        type="text" 
                        name="nombre"
                        value={formState.nombre}
                        onChange={handleChange}
                        placeholder="Tu nombre completo"
                        required
                        className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-[#5B9BB0] focus:bg-white focus:ring-0 transition-all outline-none placeholder-gray-400 text-gray-700"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-bold text-gray-700 ml-2">Email</label>
                      <input 
                        type="email" 
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        placeholder="tu@correo.com"
                        required
                        className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-[#5B9BB0] focus:bg-white focus:ring-0 transition-all outline-none placeholder-gray-400 text-gray-700"
                      />
                    </div>
                  </motion.div>

                  <motion.div className="flex flex-col gap-2" variants={itemVariants}>
                    <label className="text-sm font-bold text-gray-700 ml-2">Asunto</label>
                    <select 
                      name="asunto"
                      value={formState.asunto}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-[#5B9BB0] focus:bg-white focus:ring-0 transition-all outline-none text-gray-700 appearance-none"
                    >
                      <option value="Información General">Información General</option>
                      <option value="Programa MUN">Programa MUN</option>
                      <option value="Doble Titulación">Doble Titulación</option>
                      <option value="Orientación Internacional">Orientación Internacional</option>
                      <option value="Alianzas para Colegios">Alianzas para Colegios</option>
                    </select>
                  </motion.div>

                  <motion.div className="flex flex-col gap-2" variants={itemVariants}>
                    <label className="text-sm font-bold text-gray-700 ml-2">Mensaje</label>
                    <textarea 
                      rows="5"
                      name="mensaje"
                      value={formState.mensaje}
                      onChange={handleChange}
                      placeholder="¿En qué podemos ayudarte?"
                      required
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-[#5B9BB0] focus:bg-white focus:ring-0 transition-all outline-none placeholder-gray-400 text-gray-700 resize-none"
                    ></textarea>
                  </motion.div>

                  <motion.button 
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="mt-4 bg-[#1a3b5c] hover:bg-[#254E70] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
                    variants={itemVariants}
                  >
                    Enviar Mensaje
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                  </motion.button>

                </form>
                {/* FORM END */}

              </motion.div>

            </div>

          </div>
        </section>

        {/* 3. CTA FOOTER HOOK (Reused Style) */}
        <motion.section 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="relative py-24 px-6 md:px-12 mx-4 md:mx-8 mb-8 rounded-[3rem] overflow-hidden text-center text-white"
        >
          <div className="absolute inset-0 bg-[#1a3b5c]"></div>
          {/* Abstract Shapes */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#5B9BB0]/20 blur-[100px] rounded-full pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/10 blur-[80px] rounded-full pointer-events-none"></div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
              ¿Prefieres una reunión directa?
            </h2>
            <p className="text-blue-100 text-lg md:text-xl mb-10 leading-relaxed font-light">
              Agenda una videollamada con nuestro equipo de admisiones y recibe asesoría personalizada sin compromiso.
            </p>
            <a href="https://calendly.com/avconsulting-info/30min?month=2026-03" target="_blank" rel="noopener noreferrer">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#1a3b5c] hover:bg-blue-50 px-10 py-4 rounded-full font-bold text-lg shadow-xl transition-all"
              >
                Agendar Reunión Virtual
              </motion.button>
            </a>
          </div>
        </motion.section>

      </div>

    </>
  );
};

export default ContactoPage;